import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import './AirportSearch.css';

/**
 * AirportSearch Component
 * 
 * Beautiful autocomplete search for airports using Amadeus API
 * Features:
 * - Real-time search as user types
 * - Debounced API calls (300ms)
 * - Keyboard navigation (up/down arrows, enter, escape)
 * - Click outside to close
 * - Loading states
 * - Error handling
 * - Accessible (ARIA labels)
 */
const AirportSearch = ({ 
  label = "Airport", 
  placeholder = "Search airports...",
  value,
  onChange,
  required = false,
  className = ""
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [airports, setAirports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [error, setError] = useState(null);
  const isProgrammaticSet = useRef(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceTimeout = useRef(null);

  // Sync search term with external value prop (for auto-population)
  useEffect(() => {
    if (value && value.airportName) {
      console.log('🔄 AirportSearch: Syncing with value prop:', value);
      isProgrammaticSet.current = true;
      setSearchTerm(value.airportName);
      setIsOpen(false);  
      setAirports([]);
    } else if (!value && searchTerm) {
      // Value was cleared externally
      console.log('🔄 AirportSearch: Clearing search term');
      setSearchTerm('');
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search airports with debounce
  useEffect(() => {
    if (isProgrammaticSet.current) {
    isProgrammaticSet.current = false;
    return;  // ← skip API call
    }

    if (searchTerm.length < 2) {
      setAirports([]);
      setIsOpen(false);
      return;
    }

    // Clear previous timeout
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    // Debounce API call by 300ms
    debounceTimeout.current = setTimeout(async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await api.get('/v1/travel/airports/search', {
          params: { keyword: searchTerm }
        });

        if (response.data.success) {
          setAirports(response.data.data || []);
          setIsOpen(true);
        } else {
          setError(response.data.error || 'Search failed');
          setAirports([]);
        }
      } catch (err) {
        console.error('Airport search error:', err);
        setError('Failed to search airports');
        setAirports([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [searchTerm]);

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setSelectedIndex(-1);
  };

  // Handle airport selection
  const handleSelect = (airport) => {
    const displayValue = `${airport.name} (${airport.iataCode})`;
    setSearchTerm(displayValue);
    setIsOpen(false);
    
    // Call parent onChange with airport object
    if (onChange) {
      onChange({
        iataCode: airport.iataCode,
        name: airport.name,
        cityName: airport.cityName,
        countryCode: airport.countryCode
      });
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen || airports.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < airports.length - 1 ? prev + 1 : prev
        );
        break;
      
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && airports[selectedIndex]) {
          handleSelect(airports[selectedIndex]);
        }
        break;
      
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
      
      default:
        break;
    }
  };

  return (
    <div 
      className={`airport-search ${className}`} 
      ref={wrapperRef}
    >
      {label && (
        <label className="airport-search__label">
          {label}
          {required && <span className="required">*</span>}
        </label>
      )}
      
      <div className="airport-search__input-wrapper">
        <input
          ref={inputRef}
          type="text"
          className="airport-search__input"
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => searchTerm.length >= 2 && setIsOpen(true)}
          required={required}
          autoComplete="off"
          aria-label={label}
          aria-autocomplete="list"
          aria-controls="airport-listbox"
          aria-expanded={isOpen}
        />
        
        {loading && (
          <div className="airport-search__loading">
            <div className="spinner"></div>
          </div>
        )}
        
        {!loading && searchTerm && (
          <button
            type="button"
            className="airport-search__clear"
            onClick={() => {
              setSearchTerm('');
              setAirports([]);
              setIsOpen(false);
              if (onChange) onChange(null);
              inputRef.current?.focus();
            }}
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      {error && (
        <div className="airport-search__error">
          {error}
        </div>
      )}

      {isOpen && airports.length > 0 && (
        <ul 
          id="airport-listbox"
          className="airport-search__dropdown"
          role="listbox"
        >
          {airports.map((airport, index) => (
            <li
              key={airport.iataCode}
              className={`airport-search__item ${
                index === selectedIndex ? 'airport-search__item--selected' : ''
              }`}
              onClick={() => handleSelect(airport)}
              onMouseEnter={() => setSelectedIndex(index)}
              role="option"
              aria-selected={index === selectedIndex}
            >
              <div className="airport-search__item-main">
                <span className="airport-search__item-code">
                  {airport.iataCode}
                </span>
                <span className="airport-search__item-name">
                  {airport.name}
                </span>
              </div>
              <div className="airport-search__item-location">
                {airport.cityName}, {airport.countryCode}
              </div>
            </li>
          ))}
        </ul>
      )}

      {isOpen && searchTerm.length >= 2 && !loading && airports.length === 0 && (
        <div className="airport-search__no-results">
          No airports found for "{searchTerm}"
        </div>
      )}
    </div>
  );
};

export default AirportSearch;